import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Stack, Typography } from "@mui/material";
import { SwapHoriz as SwapHorizIcon } from "@mui/icons-material";

/**
 * Keeps a wide data table usable on narrow screens while exposing the
 * horizontal scrollbar before the table's final row is reached.
 */
const ResponsiveDataTable = ({ children, minWidth = 960, refreshKey }) => {
  const tableScrollRef = useRef(null);
  const scrollbarRef = useRef(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  const updateOverflow = useCallback(() => {
    const element = tableScrollRef.current;
    if (!element) {
      return;
    }

    setIsOverflowing(element.scrollWidth > element.clientWidth + 1);
  }, []);

  useEffect(() => {
    const element = tableScrollRef.current;
    if (!element) {
      return undefined;
    }

    let frameId = null;
    const scheduleUpdate = () => {
      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
      }
      frameId = window.requestAnimationFrame(() => {
        frameId = null;
        updateOverflow();
      });
    };

    scheduleUpdate();

    const observer =
      typeof ResizeObserver === "function"
        ? new ResizeObserver(scheduleUpdate)
        : null;
    observer?.observe(element);
    if (element.firstElementChild) {
      observer?.observe(element.firstElementChild);
    }

    return () => {
      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
      }
      observer?.disconnect();
    };
  }, [minWidth, refreshKey, updateOverflow]);

  const syncScrollbar = (event) => {
    const scrollbar = scrollbarRef.current;
    if (scrollbar && scrollbar.scrollLeft !== event.currentTarget.scrollLeft) {
      scrollbar.scrollLeft = event.currentTarget.scrollLeft;
    }
  };

  const syncTable = (event) => {
    const table = tableScrollRef.current;
    if (table && table.scrollLeft !== event.currentTarget.scrollLeft) {
      table.scrollLeft = event.currentTarget.scrollLeft;
    }
  };

  return (
    <Box sx={{ width: "100%" }}>
      {isOverflowing && (
        <>
          <Stack
            direction="row"
            spacing={0.5}
            sx={{
              display: { xs: "flex", md: "none" },
              alignItems: "center",
              mb: 0.5,
              color: "text.secondary",
            }}
          >
            <SwapHorizIcon fontSize="small" />
            <Typography variant="caption">
              Scroll horizontally to see all columns
            </Typography>
          </Stack>
          <Box
            ref={scrollbarRef}
            role="region"
            aria-label="Horizontal table scrollbar"
            onScroll={syncTable}
            sx={{
              width: "100%",
              height: 14,
              mb: 0.5,
              overflowX: "auto",
              overflowY: "hidden",
              borderRadius: 1,
              bgcolor: "action.hover",
              "&::-webkit-scrollbar": { height: 12 },
              "&::-webkit-scrollbar-track": {
                bgcolor: "action.selected",
                borderRadius: 1,
              },
              "&::-webkit-scrollbar-thumb": {
                bgcolor: "text.secondary",
                borderRadius: 1,
              },
            }}
          >
            <Box sx={{ width: minWidth, height: 1 }} />
          </Box>
        </>
      )}
      <Box
        ref={tableScrollRef}
        onScroll={syncScrollbar}
        sx={{
          width: "100%",
          overflowX: "auto",
          "&::-webkit-scrollbar": { height: 12 },
          "&::-webkit-scrollbar-thumb": {
            bgcolor: "text.secondary",
            borderRadius: 1,
          },
        }}
      >
        <Box sx={{ minWidth }}>{children}</Box>
      </Box>
    </Box>
  );
};

export default ResponsiveDataTable;
